import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { Users, Radio, Headphones, Star, MessageSquareHeart, LifeBuoy, Newspaper, CalendarClock } from "lucide-react";

const GREEN = "#4A7045";
const TERRA = "#C26A50";
const DOUGH = "#E8B96C";

function Stat({ icon: Icon, label, value, tone = "green" }) {
  const bg = tone === "terra" ? "bg-brand-terracotta/10" : tone === "dough" ? "bg-brand-dough/20" : "bg-brand/10";
  const fg = tone === "terra" ? "text-brand-terracotta" : "text-brand";
  return (
    <div className="rounded-2xl border border-brand-line bg-white p-6 lift" data-testid={`stat-${label}`}>
      <div className={`h-10 w-10 rounded-xl ${bg} flex items-center justify-center`}><Icon className={`w-5 h-5 ${fg}`} /></div>
      <p className="text-4xl font-display text-brand-ink mt-4">{value}</p>
      <p className="text-sm text-brand-muted mt-1">{label}</p>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-2xl border border-brand-line bg-white p-6">
      <h3 className="font-display text-2xl text-brand-ink mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function Metrics() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/metrics").then(({ data }) => setM(data)).catch(() => {}); }, []);
  if (!m) return <div className="px-8 py-8"><p className="text-brand-muted">Loading metrics...</p></div>;

  const pieData = [
    { name: "Claimed", value: m.cafe_user_stats.claimed },
    { name: "Unclaimed", value: m.cafe_user_stats.unclaimed },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl">
      <PageHeader title="Platform Metrics" subtitle="Cafe, podcast, and engagement insights for your leadership team." />

      <h3 className="font-display text-2xl text-brand-ink mb-4">Cafe user statistics</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={Users} label="Total team members" value={m.cafe_user_stats.total_users} />
        <Stat icon={Users} label="Claimed accounts" value={m.cafe_user_stats.claimed} />
        <Stat icon={Users} label="Unclaimed accounts" value={m.cafe_user_stats.unclaimed} tone="terra" />
        <Stat icon={Newspaper} label="Published articles" value={m.engagement_stats.article_count} tone="dough" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <Panel title="Users per store">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={m.cafe_user_stats.per_store}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" vertical={false} />
              <XAxis dataKey="store" tick={{ fontSize: 12, fill: "#5C6B61" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#5C6B61" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E0D8" }} />
              <Bar dataKey="users" fill={GREEN} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Account claim status">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                <Cell fill={GREEN} /><Cell fill={DOUGH} />
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E0D8" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 text-sm">
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full" style={{ background: GREEN }} /> Claimed</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full" style={{ background: DOUGH }} /> Unclaimed</span>
          </div>
        </Panel>
      </div>

      <h3 className="font-display text-2xl text-brand-ink mb-4">Podcast statistics</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={Radio} label="Published episodes" value={m.podcast_stats.published} />
        <Stat icon={CalendarClock} label="Scheduled" value={m.podcast_stats.scheduled} tone="dough" />
        <Stat icon={Headphones} label="Total listens" value={m.podcast_stats.total_listens} />
        <Stat icon={Star} label="Total favorites" value={m.podcast_stats.total_favorites} tone="terra" />
      </div>
      <Panel title="Top episodes by listens">
        {m.podcast_stats.top_episodes.length === 0 ? (
          <p className="text-brand-muted text-sm">No listen data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={m.podcast_stats.top_episodes} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#5C6B61" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="title" width={140} tick={{ fontSize: 11, fill: "#5C6B61" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E0D8" }} />
              <Bar dataKey="listens" fill={GREEN} radius={[0, 6, 6, 0]} />
              <Bar dataKey="favorites" fill={DOUGH} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Panel>

      <h3 className="font-display text-2xl text-brand-ink mb-4 mt-10">Engagement statistics</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={MessageSquareHeart} label="Topic suggestions" value={m.engagement_stats.feedback_count} />
        <Stat icon={LifeBuoy} label="Open tickets" value={m.engagement_stats.open_tickets} tone="terra" />
        <Stat icon={LifeBuoy} label="Total tickets" value={m.engagement_stats.total_tickets} />
        <Stat icon={Newspaper} label="Articles" value={m.engagement_stats.article_count} tone="dough" />
      </div>
    </div>
  );
}
